import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FileUpload } from "@/components/site/FileUpload";
import { errMsg } from "@/lib/api";
import { techSend, patchCache } from "@/pages/tech/offline";
import { techFileUrl } from "@/pages/tech/ui";

const MONEY = [["service_charge", "Service charge"], ["labour_charge", "Labour charge"], ["spare_parts_cost", "Spare parts"]];
const toFiles = (paths = []) => paths.map((p, i) => ({ path: p, name: `Photo ${i + 1}` }));

const Thumbs = ({ files }) => files.length > 0 && (
  <div className="flex gap-2 flex-wrap">{files.map((f) => <a key={f.path} href={techFileUrl(f.path)} target="_blank" rel="noopener noreferrer"><img src={techFileUrl(f.path)} alt={f.name} className="h-16 w-16 object-cover rounded-lg border border-border" /></a>)}</div>
);

export const JobReport = ({ job, locked }) => {
  const qc = useQueryClient();
  const r = job.report || {};
  const [f, setF] = useState({ notes: r.notes || "", parts_replaced: r.parts_replaced || "", service_charge: r.service_charge || "", labour_charge: r.labour_charge || "", spare_parts_cost: r.spare_parts_cost || "" });
  const [before, setBefore] = useState(toFiles(r.before_photos));
  const [after, setAfter] = useState(toFiles(r.after_photos));
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, ...Object.fromEntries(MONEY.map(([k]) => [k, Number(f[k] || 0)])), before_photos: before.map((x) => x.path), after_photos: after.map((x) => x.path) };
      const res = await techSend("put", `/tech/jobs/${job.id}/report`, payload, `repair report · ${job.booking_no}`);
      if (res.queued) {
        patchCache(`/tech/jobs/${job.id}`, (j) => ({ ...j, report: payload }));
        toast.message("Report saved offline — will sync when internet is back");
      } else {
        toast.success("Repair report saved");
        qc.invalidateQueries({ queryKey: ["tech", "job", job.id] });
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="card-soft p-4 space-y-4" data-testid="tech-report-form">
      <p className="font-semibold">Repair checklist</p>
      <fieldset disabled={locked} className="space-y-4 disabled:opacity-60">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2"><FileUpload kind="job" label="Before repair photos" value={before} onChange={setBefore} multiple accept="image/jpeg,image/png,image/webp" testid="tech-before-photos" /><Thumbs files={before} /></div>
        <div className="space-y-2"><FileUpload kind="job" label="After repair photos" value={after} onChange={setAfter} multiple accept="image/jpeg,image/png,image/webp" testid="tech-after-photos" /><Thumbs files={after} /></div>
      </div>
      <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Repair notes — what was the problem and what did you fix?" data-testid="tech-report-notes" />
      <Input value={f.parts_replaced} onChange={(e) => setF({ ...f, parts_replaced: e.target.value })} placeholder="Parts replaced (e.g. capacitor, gas refill)" data-testid="tech-report-parts" />
      <div className="grid grid-cols-3 gap-2">
        {MONEY.map(([k, l]) => <label key={k} className="text-xs text-muted-foreground space-y-1"><span>{l} (₹)</span><Input type="number" min="0" inputMode="decimal" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`tech-report-${k}`} /></label>)}
      </div>
      </fieldset>
      <p className="text-xs text-muted-foreground">₹{job.inspection_charge} inspection charge is added to the invoice automatically.</p>
      <p className="text-xs text-muted-foreground">Photos need internet to upload. Notes and charges can be saved offline.</p>
      <Button type="submit" disabled={busy || locked} className="rounded-full" data-testid="tech-report-save">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save report</Button>
      {locked && <p className="text-xs text-amber-600">Payment received — report is locked.</p>}
    </form>
  );
};
